import { V1_ROUTES, type V1RouteDescriptor } from "./v1-routes.js";
import { OMNI_SCOPE_DESCRIPTIONS, OMNI_OAUTH_ISSUER } from "./oauth.js";
import { PERMISSIONS, ROLES } from "./access.js";
import { intakeUploadContent } from "./intake-formats.js";

// ---------- OpenAPI 3.1 Document Data ----------

export const OPENAPI_V1_DOCUMENT = {
  openapi: "3.1.0",
  info: {
    title: "Omni Dealer API",
    version: "1.0.0",
    description:
      "REST v1 API for Omni dealer portal and AI connectors. Bearer authentication via Omni OAuth 2.0 access token or Identity Platform token. Write operations create draft quotes and customer records for human review in Omni.",
  },
  servers: [
    {
      url: OMNI_OAUTH_ISSUER,
      description: "Omni production service",
    },
  ],
  security: [
    {
      OAuth2: ["omni.read", "omni.write"],
    },
    {
      IdentityPlatform: [],
    },
  ],
  paths: {
    "/api/v1/me": {
      get: {
        operationId: "getMe",
        summary: "Current authenticated identity",
        description: "Get the current authenticated user and dealer identity.",
        security: [{ OAuth2: ["omni.read"] }, { IdentityPlatform: [] }],
        responses: {
          "200": {
            description: "Authenticated user and optional dealer profile",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/Me" },
              },
            },
          },
          "401": {
            description: "Missing or invalid bearer token",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
        },
      },
    },
    "/api/v1/customers": {
      get: {
        operationId: "listCustomers",
        summary: "List customers",
        description: "List customer summaries scoped to the caller's dealer, optionally filtered by text query.",
        security: [{ OAuth2: ["omni.read"] }, { IdentityPlatform: [] }],
        parameters: [
          {
            name: "q",
            in: "query",
            required: false,
            description: "Search filter matching customer name, phone number, or Autotask company ID",
            schema: { type: "string" },
          },
        ],
        responses: {
          "200": {
            description: "Array of customer summaries",
            content: {
              "application/json": {
                schema: {
                  type: "array",
                  items: { $ref: "#/components/schemas/CustomerSummary" },
                },
              },
            },
          },
          "401": {
            description: "Missing or invalid bearer token",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
        },
      },
      post: {
        operationId: "createCustomer",
        summary: "Create customer",
        description: "Create a new prospect customer company in Autotask with primary contact and allocated account number.",
        security: [{ OAuth2: ["omni.write"] }, { IdentityPlatform: [] }],
        requestBody: {
          required: true,
          description: "Customer prospect parameters with address and contact",
          content: {
            "application/json": {
              schema: { $ref: "#/components/schemas/CustomerInput" },
            },
          },
        },
        responses: {
          "201": {
            description: "Created customer summary with contacts",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/CustomerSummary" },
              },
            },
          },
          "400": {
            description: "Validation failure or missing required fields",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
          "401": {
            description: "Missing or invalid bearer token",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
          "403": {
            description: "Token lacks omni.write scope",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
        },
      },
    },
    "/api/v1/customers/{id}": {
      get: {
        operationId: "getCustomer",
        summary: "Get customer detail",
        description: "Get complete customer detail including contacts and billing links by Autotask company ID.",
        security: [{ OAuth2: ["omni.read"] }, { IdentityPlatform: [] }],
        parameters: [
          {
            name: "id",
            in: "path",
            required: true,
            description: "Autotask company ID of the customer",
            schema: { type: "integer" },
          },
        ],
        responses: {
          "200": {
            description: "Customer detail with contacts and billing state",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/CustomerV2" },
              },
            },
          },
          "401": {
            description: "Missing or invalid bearer token",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
          "404": {
            description: "Customer not found or outside caller scope",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
        },
      },
    },
    "/api/v1/customers/{id}/contacts": {
      post: {
        operationId: "addCustomerContact",
        summary: "Add customer contact",
        description: "Add a contact person to an existing Autotask customer.",
        security: [{ OAuth2: ["omni.write"] }, { IdentityPlatform: [] }],
        parameters: [
          {
            name: "id",
            in: "path",
            required: true,
            description: "Autotask company ID of the customer",
            schema: { type: "integer" },
          },
        ],
        requestBody: {
          required: true,
          description: "Contact input fields",
          content: {
            "application/json": {
              schema: { $ref: "#/components/schemas/ContactInput" },
            },
          },
        },
        responses: {
          "201": {
            description: "Created customer contact",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/CustomerContact" },
              },
            },
          },
          "400": {
            description: "Invalid contact fields",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
          "401": {
            description: "Missing or invalid bearer token",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
          "403": {
            description: "Token lacks omni.write scope",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
          "404": {
            description: "Customer not found",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
          "409": {
            description: "Contact with this email already exists on the company",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
        },
      },
    },
    "/api/v1/catalog": {
      get: {
        operationId: "listCatalog",
        summary: "List catalog products",
        description: "List quotable catalog products with prices and floor prices available to the caller's dealer.",
        security: [{ OAuth2: ["omni.read"] }, { IdentityPlatform: [] }],
        responses: {
          "200": {
            description: "List of quotable products",
            content: {
              "application/json": {
                schema: {
                  type: "array",
                  items: { $ref: "#/components/schemas/PricedProduct" },
                },
              },
            },
          },
          "401": {
            description: "Missing or invalid bearer token",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
        },
      },
    },
    "/api/v1/quote-templates": {
      get: {
        operationId: "listQuoteTemplates",
        summary: "List quote templates",
        description: "List reusable quote templates configured for the caller's dealer.",
        security: [{ OAuth2: ["omni.read"] }, { IdentityPlatform: [] }],
        responses: {
          "200": {
            description: "List of quote templates",
            content: {
              "application/json": {
                schema: {
                  type: "array",
                  items: { $ref: "#/components/schemas/QuoteTemplateDetail" },
                },
              },
            },
          },
          "401": {
            description: "Missing or invalid bearer token",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
        },
      },
    },
    "/api/v1/quotes": {
      get: {
        operationId: "listQuotes",
        summary: "List quotes",
        description: "List quotes within the caller's scope, optionally filtered by status.",
        security: [{ OAuth2: ["omni.read"] }, { IdentityPlatform: [] }],
        parameters: [
          {
            name: "status",
            in: "query",
            required: false,
            description: "Filter by quote status",
            schema: {
              type: "string",
              enum: ["draft", "needs_approval", "sent", "viewed", "signed", "ordered", "live", "lost"],
            },
          },
        ],
        responses: {
          "200": {
            description: "Array of quotes",
            content: {
              "application/json": {
                schema: {
                  type: "array",
                  items: { $ref: "#/components/schemas/Quote" },
                },
              },
            },
          },
          "401": {
            description: "Missing or invalid bearer token",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
        },
      },
      post: {
        operationId: "createQuoteDraft",
        summary: "Create quote draft",
        description: "Create a new quote draft. Quotes created through API are always drafts and require review in Omni.",
        security: [{ OAuth2: ["omni.write"] }, { IdentityPlatform: [] }],
        requestBody: {
          required: true,
          description: "Quote creation parameters",
          content: {
            "application/json": {
              schema: { $ref: "#/components/schemas/QuoteInput" },
            },
          },
        },
        responses: {
          "201": {
            description: "Created quote draft",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/Quote" },
              },
            },
          },
          "400": {
            description: "Validation error or unquotable product",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
          "401": {
            description: "Missing or invalid bearer token",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
          "403": {
            description: "Token lacks omni.write scope",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
        },
      },
    },
    "/api/v1/quotes/{id}": {
      get: {
        operationId: "getQuote",
        summary: "Get quote",
        description: "Get full details of a specific quote by ID.",
        security: [{ OAuth2: ["omni.read"] }, { IdentityPlatform: [] }],
        parameters: [
          {
            name: "id",
            in: "path",
            required: true,
            description: "Quote ID (e.g. Q-1001)",
            schema: { type: "string" },
          },
        ],
        responses: {
          "200": {
            description: "Quote record",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/Quote" },
              },
            },
          },
          "401": {
            description: "Missing or invalid bearer token",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
          "404": {
            description: "Quote not found",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
        },
      },
      patch: {
        operationId: "updateQuoteDraft",
        summary: "Update quote draft",
        description: "Update an existing quote draft. Modifying quotes in sent, signed, or ordered status is forbidden.",
        security: [{ OAuth2: ["omni.write"] }, { IdentityPlatform: [] }],
        parameters: [
          {
            name: "id",
            in: "path",
            required: true,
            description: "Quote ID to update",
            schema: { type: "string" },
          },
        ],
        requestBody: {
          required: true,
          description: "Quote fields to update",
          content: {
            "application/json": {
              schema: { $ref: "#/components/schemas/QuotePatchInput" },
            },
          },
        },
        responses: {
          "200": {
            description: "Updated quote",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/Quote" },
              },
            },
          },
          "400": {
            description: "Validation error or invalid status transition",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
          "401": {
            description: "Missing or invalid bearer token",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
          "403": {
            description: "Token lacks omni.write scope",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
          "404": {
            description: "Quote not found",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
        },
      },
    },
    "/api/v1/quotes/{id}/pdf": {
      get: {
        operationId: "getQuotePdf",
        summary: "Download quote PDF",
        description: "Download the generated PDF document for a quote.",
        security: [{ OAuth2: ["omni.read"] }, { IdentityPlatform: [] }],
        parameters: [
          {
            name: "id",
            in: "path",
            required: true,
            description: "Quote ID",
            schema: { type: "string" },
          },
        ],
        responses: {
          "200": {
            description: "Quote PDF document binary",
            content: {
              "application/pdf": {
                schema: {
                  type: "string",
                  format: "binary",
                },
              },
            },
          },
          "401": {
            description: "Missing or invalid bearer token",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
          "404": {
            description: "Quote not found",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
        },
      },
    },
    "/api/v1/orders": {
      get: {
        operationId: "listOrders",
        summary: "List orders",
        description: "List orders within caller scope, optionally filtered by status.",
        security: [{ OAuth2: ["omni.read"] }, { IdentityPlatform: [] }],
        parameters: [
          {
            name: "status",
            in: "query",
            required: false,
            description: "Filter by order status",
            schema: {
              type: "string",
              enum: ["in_progress", "awaiting_billing_approval", "needs_attention", "complete"],
            },
          },
        ],
        responses: {
          "200": {
            description: "Array of orders",
            content: {
              "application/json": {
                schema: {
                  type: "array",
                  items: { $ref: "#/components/schemas/Order" },
                },
              },
            },
          },
          "401": {
            description: "Missing or invalid bearer token",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
        },
      },
    },
    "/api/v1/orders/{id}": {
      get: {
        operationId: "getOrder",
        summary: "Get order",
        description: "Get full details of a specific order by ID.",
        security: [{ OAuth2: ["omni.read"] }, { IdentityPlatform: [] }],
        parameters: [
          {
            name: "id",
            in: "path",
            required: true,
            description: "Order ID (e.g. O-1001)",
            schema: { type: "string" },
          },
        ],
        responses: {
          "200": {
            description: "Order record",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/Order" },
              },
            },
          },
          "401": {
            description: "Missing or invalid bearer token",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
          "404": {
            description: "Order not found",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
        },
      },
      patch: {
        operationId: "setOrderLineNumbers",
        summary: "Set each line's Telephone Number",
        description:
          "Set the Telephone Number on each order line whose OneBill plan needs one, as the seller. Phoneware confirms them at billing approval. Each entry sets its line's number as 10 digits; an empty number takes it off, and lines not listed keep theirs. Refused once billing is approved.",
        security: [{ OAuth2: ["omni.write"] }, { IdentityPlatform: [] }],
        parameters: [
          {
            name: "id",
            in: "path",
            required: true,
            description: "Order ID (e.g. O-1001)",
            schema: { type: "string" },
          },
        ],
        requestBody: {
          required: true,
          description: "The order's updatedAt and the numbers to set",
          content: {
            "application/json": {
              schema: { $ref: "#/components/schemas/OrderLineNumbersInput" },
            },
          },
        },
        responses: {
          "200": {
            description: "The order with each line's Telephone Number",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/Order" },
              },
            },
          },
          "400": {
            description: "A number that isn't 10 digits, on a line that doesn't take one, or a field the API doesn't set; the error names the line",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/LineNumbersError" },
              },
            },
          },
          "401": {
            description: "Missing or invalid bearer token",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
          "403": {
            description: "Token lacks omni.write scope",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
          "404": {
            description: "Order not found",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
          "409": {
            description: "The order changed since expectedUpdatedAt, billing is already approved, or the order is complete",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
          "502": {
            description: "OneBill can't say which lines need a Telephone Number",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
        },
      },
    },
    "/api/v1/quote-intakes": {
      post: {
        operationId: "uploadQuoteIntake",
        summary: "Upload quote document",
        description: "Upload a quote document as raw PDF for extraction and catalog matching.",
        security: [{ OAuth2: ["omni.write"] }, { IdentityPlatform: [] }],
        parameters: [
          {
            name: "X-Filename",
            in: "header",
            required: false,
            description: "Original filename of the uploaded PDF",
            schema: { type: "string" },
          },
          {
            name: "dealerId",
            in: "query",
            required: false,
            description: "Target dealer ID (ops callers only)",
            schema: { type: "string" },
          },
        ],
        requestBody: {
          required: true,
          description: "Raw quote file bytes",
          content: intakeUploadContent(),
        },
        responses: {
          "201": {
            description: "Created quote intake record",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/QuoteIntakeView" },
              },
            },
          },
          "400": {
            description: "Bad request or missing required parameters",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
          "401": {
            description: "Missing or invalid bearer token",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
          "403": {
            description: "Forbidden: insufficient scope or permissions",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
          "413": {
            description: "File exceeds 10MB limit",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
          "415": {
            description: "Invalid document content type. Expected application/pdf",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
        },
      },
    },
    "/api/v1/quote-intakes/text": {
      post: {
        operationId: "submitQuoteIntakeText",
        summary: "Submit pasted quote text",
        description: "Submit pasted quote text or a conversation transcript for extraction and catalog matching.",
        security: [{ OAuth2: ["omni.write"] }, { IdentityPlatform: [] }],
        requestBody: {
          required: true,
          description: "Pasted text and kind",
          content: {
            "application/json": {
              schema: { $ref: "#/components/schemas/QuoteIntakeTextRequest" },
            },
          },
        },
        responses: {
          "201": {
            description: "Created quote intake",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/QuoteIntakeView" },
              },
            },
          },
          "200": {
            description: "Existing intake for the same source",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/QuoteIntakeView" },
              },
            },
          },
          "400": {
            description: "Text is required, textKind is invalid, or the text could not be parsed",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
          "413": {
            description: "Text is over 200,000 characters",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
        },
      },
    },
    "/api/v1/quote-intakes/kqm": {
      post: {
        operationId: "importKqmQuote",
        summary: "Import a KQM quote",
        description: "Import a KQM quote by link, id, or quote number. Phoneware staff only.",
        security: [{ OAuth2: ["omni.write"] }, { IdentityPlatform: [] }],
        requestBody: {
          required: true,
          description: "KQM quote reference",
          content: {
            "application/json": {
              schema: { $ref: "#/components/schemas/QuoteIntakeKqmRequest" },
            },
          },
        },
        responses: {
          "201": {
            description: "Created quote intake",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/QuoteIntakeView" },
              },
            },
          },
          "400": {
            description: "The quote reference could not be parsed, or KQM has more than one quote with that number",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
          "403": {
            description: "Only Phoneware staff can import KQM quotes",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
          "404": {
            description: "KQM has no quote for that reference",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
        },
      },
    },
    "/api/v1/quote-intakes/{id}": {
      get: {
        operationId: "getQuoteIntake",
        summary: "Get quote intake",
        description: "Get details, extraction results, matching lines, and status for a quote intake document.",
        security: [{ OAuth2: ["omni.read"] }, { IdentityPlatform: [] }],
        parameters: [
          {
            name: "id",
            in: "path",
            required: true,
            description: "Quote intake unique identifier",
            schema: { type: "string" },
          },
        ],
        responses: {
          "200": {
            description: "Quote intake details",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/QuoteIntakeView" },
              },
            },
          },
          "401": {
            description: "Missing or invalid bearer token",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
          "404": {
            description: "Quote intake not found",
            content: {
              "application/json": {
                schema: { $ref: "#/components/schemas/ApiError" },
              },
            },
          },
        },
      },
    },
    "/api/v1/openapi.json": {
      get: {
        operationId: "getOpenApiJson",
        summary: "OpenAPI specification document",
        description: "Returns the OpenAPI 3.1 specification document describing the Omni Dealer REST API v1.",
        responses: {
          "200": {
            description: "OpenAPI 3.1 document as JSON data",
            content: {
              "application/json": {
                schema: {
                  type: "object",
                  description: "OpenAPI specification document",
                },
              },
            },
          },
        },
      },
    },
  },
  components: {
    securitySchemes: {
      OAuth2: {
        type: "oauth2",
        description: "Omni OAuth 2.0 authorization server",
        flows: {
          authorizationCode: {
            authorizationUrl: `${OMNI_OAUTH_ISSUER}/authorize`,
            tokenUrl: `${OMNI_OAUTH_ISSUER}/token`,
            scopes: OMNI_SCOPE_DESCRIPTIONS,
          },
        },
      },
      IdentityPlatform: {
        type: "http",
        scheme: "bearer",
        bearerFormat: "JWT",
        description: "Google Cloud Identity Platform session token for interactive web sessions",
      },
    },
    schemas: {
      ApiError: {
        type: "object",
        properties: {
          error: { type: "string", description: "Error explanation" },
        },
        required: ["error"],
      },
      Me: {
        type: "object",
        properties: {
          user: {
            type: "object",
            properties: {
              email: { type: "string" },
              name: { type: "string" },
              role: { type: "string", enum: ROLES },
              scope: { type: "string", enum: ["all", "own"] },
            },
            required: ["email", "name", "role", "scope"],
          },
          dealer: {
            type: ["object", "null"],
            properties: {
              id: { type: "string" },
              name: { type: "string" },
              model: { type: "string", enum: ["agent", "wholesale"] },
            },
          },
          permissions: {
            type: "array",
            items: { type: "string", enum: PERMISSIONS },
            description: "Permissions granted to the user based on their role",
          },
        },
        required: ["user", "permissions"],
      },
      CustomerSummary: {
        type: "object",
        properties: {
          autotaskCompanyId: { type: "integer" },
          name: { type: "string" },
          phone: { type: ["string", "null"] },
          accountNumber: { type: ["string", "null"] },
          billingState: { type: "string", enum: ["unreviewed", "linked", "create", "not_billed"] },
          createdBy: { type: ["string", "null"] },
          createdVia: { type: ["object", "null"] },
        },
        required: ["autotaskCompanyId", "name"],
      },
      CustomerV2: {
        type: "object",
        properties: {
          autotaskCompanyId: { type: "integer" },
          name: { type: "string" },
          phone: { type: ["string", "null"] },
          accountNumber: { type: ["string", "null"] },
          address: { type: ["object", "null"] },
          contacts: { type: "array", items: { $ref: "#/components/schemas/CustomerContact" } },
          billing: { type: "object" },
        },
        required: ["autotaskCompanyId", "name", "contacts"],
      },
      CustomerContact: {
        type: "object",
        properties: {
          id: { type: "integer" },
          firstName: { type: "string" },
          lastName: { type: "string" },
          email: { type: "string" },
          phone: { type: ["string", "null"] },
          title: { type: ["string", "null"] },
          role: { type: "string" },
        },
        required: ["id", "firstName", "lastName", "email"],
      },
      CustomerInput: {
        type: "object",
        properties: {
          name: { type: "string" },
          phone: { type: "string" },
          address: {
            type: "object",
            properties: {
              line1: { type: "string" },
              line2: { type: "string" },
              city: { type: "string" },
              state: { type: "string" },
              postalCode: { type: "string" },
            },
            required: ["line1", "city", "state", "postalCode"],
          },
          contact: {
            type: "object",
            properties: {
              firstName: { type: "string" },
              lastName: { type: "string" },
              email: { type: "string" },
              phone: { type: "string" },
              title: { type: "string" },
            },
            required: ["firstName", "lastName", "email"],
          },
        },
        required: ["name", "phone", "address", "contact"],
      },
      ContactInput: {
        type: "object",
        properties: {
          firstName: { type: "string" },
          lastName: { type: "string" },
          email: { type: "string" },
          phone: { type: "string" },
          title: { type: "string" },
          role: { type: "string", enum: ["primary", "billing", "technical", "owner"] },
        },
        required: ["firstName", "lastName", "email"],
      },
      PricedProduct: {
        type: "object",
        properties: {
          id: { type: "string" },
          code: { type: "string" },
          name: { type: "string" },
          description: { type: "string" },
          category: { type: "string" },
          billing: { type: "string", enum: ["monthly", "one_time"] },
          unitPrice: { type: "integer" },
          floorPrice: { type: ["integer", "null"] },
        },
        required: ["id", "code", "name", "billing", "unitPrice"],
      },
      QuoteTemplateDetail: {
        type: "object",
        properties: {
          id: { type: "string" },
          name: { type: "string" },
          description: { type: "string" },
          term: { type: ["integer", "string"] },
          lineCount: { type: "integer" },
        },
        required: ["id", "name"],
      },
      Quote: {
        type: "object",
        properties: {
          id: { type: "string" },
          number: { type: "string" },
          customerId: { type: "integer" },
          customerName: { type: "string" },
          status: { type: "string" },
          type: { type: "string", enum: ["new", "change", "renewal"] },
          term: { type: ["integer", "string"] },
          lines: { type: "array", items: { type: "object" } },
          totals: {
            type: "object",
            properties: {
              monthly: { type: "integer" },
              oneTime: { type: "integer" },
              dueAtSigning: { type: "integer" },
            },
            required: ["monthly", "oneTime"],
          },
          createdVia: { type: "object" },
          intakeId: {
            type: ["string", "null"],
            description: "Originating quote intake ID when created from an uploaded quote document",
          },
        },
        required: ["id", "number", "customerId", "status", "lines", "totals"],
      },
      QuoteInput: {
        type: "object",
        properties: {
          customerId: { type: "integer" },
          term: { type: ["integer", "string"] },
          type: { type: "string", enum: ["new", "change", "renewal"] },
          templateId: { type: "string" },
          intakeId: {
            type: ["string", "null"],
            description: "Originating quote intake ID when created from an uploaded quote document",
          },
          lines: {
            type: "array",
            items: {
              type: "object",
              properties: {
                productId: { type: "string" },
                qty: { type: "integer" },
                unitPrice: { type: "integer" },
              },
              required: ["productId", "qty", "unitPrice"],
            },
          },
          internalNotes: { type: "string" },
        },
        required: ["customerId", "term"],
      },
      QuotePatchInput: {
        type: "object",
        properties: {
          term: { type: ["integer", "string"] },
          lines: {
            type: "array",
            items: {
              type: "object",
              properties: {
                productId: { type: "string" },
                qty: { type: "integer" },
                unitPrice: { type: "integer" },
              },
              required: ["productId", "qty", "unitPrice"],
            },
          },
          internalNotes: { type: "string" },
        },
      },
      Order: {
        type: "object",
        properties: {
          id: { type: "string" },
          number: { type: "string" },
          quoteId: { type: "string" },
          customerId: { type: "integer" },
          customerName: { type: "string" },
          status: { type: "string" },
          kind: { type: "string" },
          tickets: { type: "array", items: { type: "object" } },
          dueDate: { type: ["string", "null"] },
          numbersNote: { type: "string" },
          lineNumbers: {
            type: "array",
            description: "Each line's Telephone Number as the seller set it, for the lines whose OneBill plan needs one",
            items: { $ref: "#/components/schemas/OrderLineNumber" },
          },
          updatedAt: { type: "string", description: "When the order last changed; send it back as expectedUpdatedAt" },
        },
        required: ["id", "number", "quoteId", "customerId", "status"],
      },
      OrderLineNumber: {
        type: "object",
        properties: {
          line: { type: "integer", description: "The line's position on the signed quote, from 0" },
          number: { type: "string", description: "The line's Telephone Number, 10 digits" },
        },
        required: ["line", "number"],
      },
      OrderLineNumbersInput: {
        type: "object",
        properties: {
          expectedUpdatedAt: { type: "string", description: "The order's updatedAt as you read it" },
          lineNumbers: {
            type: "array",
            description: "One entry per line to set; an empty number takes the line's number off",
            items: { $ref: "#/components/schemas/OrderLineNumber" },
          },
        },
        required: ["expectedUpdatedAt", "lineNumbers"],
        additionalProperties: false,
      },
      LineNumbersError: {
        type: "object",
        properties: {
          error: { type: "string", description: "Error explanation, naming the line" },
          line: { type: ["integer", "null"], description: "The line the error is about, from 0, or null" },
        },
        required: ["error"],
      },
      QuoteIntakeTextRequest: {
        type: "object",
        properties: {
          text: { type: "string", description: "Pasted quote text or transcript" },
          textKind: { type: "string", enum: ["document", "transcript"] },
          title: { type: "string", description: "Optional title" },
          dealerId: { type: "string", description: "Target dealer. Required for Phoneware staff." },
        },
        required: ["text", "textKind"],
        additionalProperties: false,
      },
      QuoteIntakeKqmRequest: {
        type: "object",
        properties: {
          quote: { type: "string", description: "KQM quote link, id, or quote number" },
          dealerId: { type: "string", description: "Target dealer. Required for Phoneware staff." },
        },
        required: ["quote"],
        additionalProperties: false,
      },
      QuoteIntakeView: {
        type: "object",
        description: "Intake as a person reviews it. Dealer views never include a switched-off product.",
        properties: {
          id: { type: "string" },
          number: { type: "string" },
          dealerId: { type: "string" },
          state: { type: "string", enum: ["extracting", "ready", "failed", "confirmed", "discarded"] },
          error: { type: ["string", "null"] },
          source: { type: "object", additionalProperties: true },
          parts: { type: ["array", "null"], items: { type: "object", additionalProperties: true } },
          reviewUrl: { type: "string" },
        },
        required: ["id", "number", "dealerId", "state", "source", "reviewUrl"],
      },
      QuoteIntake: {
        type: "object",
        properties: {
          id: { type: "string", description: "Quote intake ID" },
          number: { type: "string", description: "Human number (e.g. I-1001)" },
          dealerId: { type: "string", description: "Dealer ID" },
          state: {
            type: "string",
            enum: ["extracting", "ready", "failed", "confirmed", "discarded"],
          },
          error: { type: ["string", "null"] },
          source: {
            type: "object",
            properties: {
              filename: { type: "string" },
              bytes: { type: "integer" },
              sha256: { type: "string" },
            },
            required: ["filename", "bytes", "sha256"],
          },
        },
        required: ["id", "number", "dealerId", "state"],
      },
    },
  },
} as const;

// ---------- Bidirectional Sync Validation ----------

export interface OpenApiSyncReport {
  inSync: boolean;
  missingInDoc: string[];
  missingInRoutes: string[];
}

/**
 * Validates that every route in the v1 route table appears in the OpenAPI document,
 * and every documented route in the OpenAPI document exists in the v1 route table.
 */
export function validateOpenApiSync(
  routes: readonly V1RouteDescriptor[] = V1_ROUTES,
  doc: typeof OPENAPI_V1_DOCUMENT = OPENAPI_V1_DOCUMENT,
): OpenApiSyncReport {
  const missingInDoc: string[] = [];
  const missingInRoutes: string[] = [];

  const routeKeys: Record<string, true> = {};
  for (const r of routes) {
    const key = `${r.method.toUpperCase()} ${r.openapiPath}`;
    routeKeys[key] = true;
  }

  const docKeys: Record<string, true> = {};
  for (const [pathKey, pathItem] of Object.entries(doc.paths)) {
    for (const methodKey of Object.keys(pathItem)) {
      const key = `${methodKey.toUpperCase()} ${pathKey}`;
      docKeys[key] = true;
    }
  }

  for (const k of Object.keys(routeKeys)) {
    if (!docKeys[k]) {
      missingInDoc.push(k);
    }
  }

  for (const k of Object.keys(docKeys)) {
    if (!routeKeys[k]) {
      missingInRoutes.push(k);
    }
  }

  return {
    inSync: missingInDoc.length === 0 && missingInRoutes.length === 0,
    missingInDoc,
    missingInRoutes,
  };
}
